import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { getApiUrl } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  unitPreference: string;
  needsOnboarding: boolean;
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setUnitPreference: (pref: string) => void;
  setNeedsOnboarding: (val: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unitPreference, setUnitPreference] = useState<string>('imperial');
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const checkAuth = async () => {
    try {
      const response = await api.checkAuth();
      if (response.authenticated && response.user) {
        setUser(response.user);
        api.updateTimezone().catch(() => {}); // fire-and-forget; profile may not exist on first login
        if (response.unit_preference) {
          setUnitPreference(response.unit_preference);
        }
        setNeedsOnboarding(!!response.needs_onboarding);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = () => {
    const apiUrl = getApiUrl(); // Compute dynamically
    const redirectUrl = `${apiUrl}/auth/strava/login`;
    console.log('=== DEBUG LOGIN ===');
    console.log('Current hostname:', window.location.hostname);
    console.log('Current origin:', window.location.origin);
    console.log('Computed API_URL:', apiUrl);
    console.log('Redirecting to:', redirectUrl);
    console.log('===================');

    window.location.href = redirectUrl;
  };

  const logout = async () => {
    try {
      await api.logout();
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    unitPreference,
    needsOnboarding,
    login,
    logout,
    checkAuth,
    setUnitPreference,
    setNeedsOnboarding,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
